/** Admin — Media UI (single/multiple upload, preview, associate, delete). */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, Trash2, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { listMedia, uploadMedia, deleteMedia, mediaUrl } from '@/services/api/media';
import { formatApiError } from '@/services/api/client';
import { ADMIN_MANAGE } from '@/constants/testIds';

export default function MediaAdmin() {
	const qc = useQueryClient();
	const [ownerType, setOwnerType] = useState('');
	const [ownerId, setOwnerId] = useState('');
	const [busy, setBusy] = useState(false);
	const { data = [] } = useQuery({ queryKey: ['media'], queryFn: listMedia });
	const inv = () => qc.invalidateQueries({ queryKey: ['media'] });

	const delM = useMutation({ mutationFn: deleteMedia, onSuccess: () => { toast.success('Media eliminato'); inv(); }, onError: (e) => toast.error(formatApiError(e.response?.data?.detail)) });

	const onFiles = async (e) => {
		const files = Array.from(e.target.files || []);
		if (!files.length) return;
		setBusy(true);
		let ok = 0;
		for (const file of files) {
			try {
				await uploadMedia(file, { ownerType: ownerType || undefined, ownerId: ownerId || undefined });
				ok += 1;
			} catch (err) {
				toast.error(`${file.name}: ${formatApiError(err.response?.data?.detail)}`);
			}
		}
		setBusy(false);
		e.target.value = '';
		if (ok) { toast.success(`${ok} file caricati`); inv(); }
	};

	return (
		<div data-testid={ADMIN_MANAGE.mediaPage} className="mx-auto max-w-5xl">
			<h1 className="text-3xl font-extrabold" style={{ fontFamily: "'Poppins', sans-serif" }}>Media</h1>
			<p className="mt-1 text-slate-400">Carica immagini/documenti e associali facoltativamente a un contenuto.</p>

			<div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
				<div className="grid gap-3 sm:grid-cols-2">
					<div>
						<Label>Associa a (opzionale)</Label>
						<select value={ownerType} onChange={(e) => setOwnerType(e.target.value)} className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/50 px-3 py-2 text-sm">
							<option value="">Nessuna associazione</option>
							<option value="event">Evento</option>
							<option value="place">Luogo</option>
							<option value="activity">Attività</option>
						</select>
					</div>
					<div><Label>ID contenuto (opzionale)</Label><Input value={ownerId} onChange={(e) => setOwnerId(e.target.value)} placeholder="id evento/luogo/attività" className="bg-slate-950/50 border-slate-700" /></div>
				</div>
				<label className="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-slate-600 py-8 text-slate-300 hover:border-emerald-400/40">
					{busy ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} className="text-emerald-300" />}
					<span>{busy ? 'Caricamento…' : 'Seleziona uno o più file'}</span>
					<input type="file" multiple className="hidden" onChange={onFiles} data-testid={ADMIN_MANAGE.mediaUploadInput} disabled={busy} />
				</label>
			</div>

			<div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
				{data.map((m) => (
					<div key={m.id} className="group relative overflow-hidden rounded-xl border border-slate-800 bg-slate-900/40">
						{m.media_type === 'image'
							? <img src={mediaUrl(m.url)} alt={m.filename} onError={(e) => { e.currentTarget.style.display = 'none'; }} className="h-32 w-full bg-slate-800 object-cover" />
							: <div className="flex h-32 items-center justify-center text-xs text-slate-500">{m.mime_type}</div>}
						<div className="p-2"><p className="truncate text-xs text-slate-300">{m.filename}</p>{m.owner_type && <p className="text-[10px] text-emerald-300">↳ {m.owner_type}</p>}</div>
						<button onClick={() => delM.mutate(m.id)} className="absolute right-1.5 top-1.5 rounded-md bg-slate-950/70 p-1 text-rose-300 opacity-0 transition-opacity group-hover:opacity-100"><Trash2 size={13} /></button>
					</div>
				))}
				{data.length === 0 && <p className="col-span-full text-slate-500">Nessun media caricato.</p>}
			</div>
		</div>
	);
}
